import React, { useEffect, useState, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  Skeleton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Star as StarIcon,
  RateReview as RateReviewIcon,
} from '@mui/icons-material';
import ReviewCard from './ReviewCard';
import {
  fetchApprovedReviews,
  fetchReviewStats,
  Review,
  ReviewStats,
} from '../api/reviewsApi';
import { brand } from '../../../config/brand';

const ReviewsCarousel: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const scrollRef = useRef<HTMLDivElement>(null);

  const [reviews, setReviews] = useState<Review[]>([]);
  const [stats, setStats] = useState<ReviewStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [reviewsRes, statsRes] = await Promise.all([
          fetchApprovedReviews({ limit: 12 }),
          fetchReviewStats(),
        ]);
        setReviews(reviewsRes.reviews);
        setStats(statsRes);
      } catch (err) {
        console.error('Failed to load reviews:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const cardWidth = isMobile ? 300 : 360;
    const scrollAmount = direction === 'left' ? -cardWidth : cardWidth;
    scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  };

  const hasReviews = !loading && reviews.length > 0;

  return (
    <Box sx={{ py: { xs: 6, md: 10 } }}>
      <Container maxWidth="lg">
        {/* Section header */}
        <Box
          sx={{
            textAlign: 'center',
            mb: 6,
          }}
        >
          <Typography
            variant="h3"
            component="h2"
            fontWeight="bold"
            gutterBottom
          >
            What Our Users Say
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: 600, mx: 'auto', mb: 3 }}
          >
            Trusted by developers worldwide to build production-ready
            applications faster.
          </Typography>

          {/* Stats */}
          {hasReviews && stats && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: { xs: 3, md: 6 },
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <StarIcon sx={{ color: brand.colors.fire, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" fontWeight={800} lineHeight={1}>
                    {stats.averageRating.toFixed(1)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Average Rating
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="h4" fontWeight={800} lineHeight={1}>
                  {stats.totalReviews}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Reviews
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {/* Carousel */}
        {(loading || hasReviews) && (
          <Box sx={{ position: 'relative' }}>
            {/* Scroll buttons */}
            {!isMobile && (
              <>
                <IconButton
                  onClick={() => scroll('left')}
                  sx={{
                    position: 'absolute',
                    left: -20,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 2,
                    bgcolor: 'background.paper',
                    boxShadow: 3,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <ChevronLeftIcon />
                </IconButton>
                <IconButton
                  onClick={() => scroll('right')}
                  sx={{
                    position: 'absolute',
                    right: -20,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 2,
                    bgcolor: 'background.paper',
                    boxShadow: 3,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </>
            )}

            {/* Scrollable row */}
            <Box
              ref={scrollRef}
              sx={{
                display: 'flex',
                gap: 3,
                overflowX: 'auto',
                scrollSnapType: 'x mandatory',
                pb: 2,
                px: 1,
                '&::-webkit-scrollbar': { height: 6 },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: `rgba(${brand.colorsRgb.fire}, 0.3)`,
                  borderRadius: 3,
                },
              }}
            >
              {loading
                ? Array.from({ length: 4 }).map((_, i) => (
                    <Box
                      key={i}
                      sx={{
                        minWidth: isMobile ? 280 : isTablet ? 320 : 340,
                        scrollSnapAlign: 'start',
                      }}
                    >
                      <Skeleton
                        variant="rounded"
                        height={240}
                        sx={{ borderRadius: 3 }}
                      />
                    </Box>
                  ))
                : reviews.map((review) => (
                    <Box
                      key={review._id}
                      sx={{
                        minWidth: isMobile ? 280 : isTablet ? 320 : 340,
                        scrollSnapAlign: 'start',
                      }}
                    >
                      <ReviewCard review={review} />
                    </Box>
                  ))}
            </Box>
          </Box>
        )}

        {/* Modest CTA */}
        <Box sx={{ textAlign: 'center', mt: hasReviews ? 5 : 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {hasReviews ? 'Had a great experience?' : `Be the first to share your experience with ${brand.productName}.`}
          </Typography>
          <Button
            variant="outlined"
            size="medium"
            component={RouterLink}
            to="/reviews/submit"
            startIcon={<RateReviewIcon />}
            sx={{
              px: 3,
              fontWeight: 600,
              borderColor: brand.colors.fire,
              color: brand.colors.fire,
              '&:hover': {
                borderColor: brand.colors.fireDark,
                bgcolor: `rgba(${brand.colorsRgb.fire}, 0.04)`,
              },
            }}
          >
            Leave a Review
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default ReviewsCarousel;
